// frontend/src/pages/RestDayMonitoring/components/RestDayAlerts.js
import React from 'react';
import { 
  AlertTriangle, 
  AlertOctagon, 
  XCircle, 
  CheckCircle,
  Clock,
  Eye
} from 'lucide-react';
import { StatusBadge } from '../../../components/UI/StatusBadge';

/**
 * RestDayAlerts - Display active rest day compliance alerts.
 * 
 * Shows:
 * 1. Active Threshold Alerts (watch / warning / critical / violated)
 * 2. MOM Guidelines Reference (Employment Act s36)
 * 
 * Design: admin red theme per design-system-reference.md
 */
const RestDayAlerts = ({ 
  alerts, 
  loading, 
  onAcknowledge,
  currentUser
}) => {
  // Severity config for alert cards (icon/card styling only; badge uses StatusBadge)
  const getSeverityConfig = (alertType) => {
    const configs = {
      violated: {
        icon: XCircle,
        bgColor: 'bg-red-50',
        iconBg: 'bg-red-100',
        iconColor: 'text-red-600',
        borderColor: 'border-red-200',
      },
      critical: {
        icon: AlertOctagon,
        bgColor: 'bg-orange-50',
        iconBg: 'bg-orange-100',
        iconColor: 'text-orange-600',
        borderColor: 'border-orange-200',
      },
      warning: {
        icon: AlertTriangle,
        bgColor: 'bg-yellow-50',
        iconBg: 'bg-yellow-100',
        iconColor: 'text-yellow-600',
        borderColor: 'border-yellow-200',
      },
      watch: {
        icon: Eye,
        bgColor: 'bg-blue-50',
        iconBg: 'bg-blue-100',
        iconColor: 'text-blue-600',
        borderColor: 'border-blue-200',
      },
    };
    return configs[alertType] || configs.watch;
  };

  // Loading skeleton
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-2xl shadow-sm border p-6">
          <div className="h-6 bg-gray-200 rounded w-48 mb-4 animate-pulse" />
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="p-4 bg-gray-50 rounded-xl animate-pulse">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-gray-200 rounded-full mr-4" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-1/3" />
                    <div className="h-3 bg-gray-200 rounded w-1/4" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Active Alerts */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <AlertTriangle className="w-5 h-5 mr-2 text-red-600" />
                Active Rest Day Alerts
              </h3>
              <p className="text-xs text-gray-500 mt-1">
                Open threshold crossings only. Alerts clear automatically when the worker rests
                and the streak drops below the tier. Days 7–8 are employee-only Watch;
                progressive RO / HR / HOD escalation begins at day 9.
              </p>
            </div>
            <span className="inline-flex items-center justify-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
              {alerts.filter(a => a.alertStatus === 'active').length} unacknowledged
            </span>
          </div>
        </div>
        
        <div className="space-y-3">
          {alerts.length === 0 ? (
            <div className="p-8 text-center">
              <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
              <h4 className="text-lg font-medium text-gray-900 mb-2">No Active Alerts</h4>
              <p className="text-sm text-gray-500">All employees are within the 12-day rest day interval limit</p>
            </div>
          ) : (
            alerts.map(alert => {
              const tier = alert.tier || 'watch';
              const config = getSeverityConfig(tier);
              const Icon = config.icon;
              
              return (
                <div 
                  key={alert.id} 
                  className={`p-4 ${config.bgColor} border border-gray-200 rounded-xl`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start">
                      <div className={`w-10 h-10 ${config.iconBg} rounded-full flex items-center justify-center flex-shrink-0`}>
                        <Icon className={`w-5 h-5 ${config.iconColor}`} />
                      </div>
                      <div className="ml-4">
                        <div className="flex items-center mb-1">
                          <h4 className="text-sm font-medium text-gray-900">
                            {alert.employeeName}
                            {alert.employeeId && (
                              <span className="ml-2 text-xs font-normal text-gray-500">
                                · {alert.employeeId}
                              </span>
                            )}
                          </h4>
                          <span className="ml-3">
                            <StatusBadge status={tier} />
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">{alert.consecutiveWorkingDays} consecutive working days</span>
                          <span className="mx-2">•</span>
                          {tier === 'violated' ? 'Exceeded the 12-day MOM limit' :
                           tier === 'critical' ? 'At the 12-day ceiling — rest day required before working again' :
                           tier === 'warning' ? 'Approaching the 12-day limit' :
                           'Monitoring'}
                        </p>
                        <div className="flex items-center mt-2 text-xs text-gray-500">
                          <Clock className="w-3 h-3 mr-1" />
                          Triggered: {new Date(alert.triggeredAt).toLocaleDateString('en-SG', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                          })}
                          {alert.department && (
                            <>
                              <span className="mx-2">•</span>
                              {alert.department}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2 ml-4">
                      {alert.alertStatus === 'acknowledged' ? (
                        <StatusBadge status="acknowledged" showIcon />
                      ) : (
                        <button
                          onClick={() => onAcknowledge?.(alert.id, currentUser?.staff_id)}
                          className="inline-flex items-center px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-200 rounded-full hover:bg-red-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1"
                        >
                          Acknowledge
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* MOM Guidelines Reference */}
      <div className="bg-blue-50 rounded-2xl p-6 border border-blue-200">
        <h3 className="text-lg font-semibold text-blue-900 mb-4">
          📋 MOM Employment Act s36 — Rest Day Requirement
        </h3>
        <p className="text-sm text-gray-700 mb-4">
          Every employee is entitled to at least <strong>1 rest day per week</strong>. The maximum interval between 
          two rest days must not exceed <strong>12 consecutive days</strong>.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          <div className="bg-white rounded-xl p-4 border border-blue-100">
            <div className="flex items-center mb-2">
              <div className="w-3 h-3 rounded-full bg-amber-500 mr-2" />
              <h4 className="font-medium text-gray-900">Watch (7–8 days)</h4>
            </div>
            <p className="text-sm text-gray-600">Begin monitoring. Standard for a 6-day work week.</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-blue-100">
            <div className="flex items-center mb-2">
              <div className="w-3 h-3 rounded-full bg-orange-500 mr-2" />
              <h4 className="font-medium text-gray-900">Warning (9–11 days)</h4>
            </div>
            <p className="text-sm text-gray-600">Approaching the limit — plan a rest day soon.</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-blue-100">
            <div className="flex items-center mb-2">
              <div className="w-3 h-3 rounded-full bg-red-400 mr-2" />
              <h4 className="font-medium text-gray-900">Critical (12 days)</h4>
            </div>
            <p className="text-sm text-gray-600">At the MOM ceiling — rest day required before working again.</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-blue-100">
            <div className="flex items-center mb-2">
              <div className="w-3 h-3 rounded-full bg-red-600 mr-2" />
              <h4 className="font-medium text-gray-900">Violated (13+ days)</h4>
            </div>
            <p className="text-sm text-gray-600">Exceeded the 12-day limit. Mandatory immediate rest day.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RestDayAlerts;
